/**
 * PublicChatWidget — Phase B-Lite
 *
 * Floating chat widget mounted globally on marketing pages.
 * - Sticky bubble bottom-right (hidden on /admin/* and /members/*).
 * - Click to expand into a 380×560 chat panel.
 * - Backend handles Pat → Sam / Jordan routing + RAG + Emergent LLM.
 * - Progressive lead capture: when backend signals `should_capture_lead`,
 *   the input is swapped for a name/email/phone form.
 * - Session id persisted in localStorage so refresh continues the chat.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  CheckCircle2,
  Loader2,
  MessageSquare,
  Send,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const STORAGE_KEY = "br_public_chat_session_v1";

// Resolve API base at runtime so the widget works both in preview
// (same-origin) and on the production custom domain (blackrdg.com),
// where REACT_APP_BACKEND_URL would point to a different host and
// cross-origin requests would either fail CORS or break cookies.
function resolveApiBase() {
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    return window.location.origin;
  }
  return (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
}
const API_BASE = resolveApiBase();

const AGENT_LABELS = {
  "pat-harper": "Pat",
  "sam-torres": "Sam",
  "jordan-hale": "Jordan",
};

const AGENT_ROLES = {
  "pat-harper": "Concierge",
  "sam-torres": "Sales",
  "jordan-hale": "Plans",
};

function classifyHidden(pathname) {
  if (!pathname) return false;
  if (pathname.startsWith("/admin")) return true;
  if (pathname.startsWith("/members")) return true;
  return false;
}

async function jsonFetch(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      detail = body.detail || detail;
    } catch (_err) {
      // swallow
    }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

function MessageBubble({ message }) {
  const isUser = message.role === "user";
  const agentName = AGENT_LABELS[message.agent_slug] || "Black Ridge";
  return (
    <div
      data-testid={`chat-message-${message.role}`}
      className={`flex flex-col ${isUser ? "items-end" : "items-start"} gap-1`}
    >
      {!isUser && (
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 pl-1">
          {agentName}
          {message.agent_slug ? (
            <span className="ml-1 normal-case tracking-normal text-zinc-600">
              · {AGENT_ROLES[message.agent_slug] || "AI"}
            </span>
          ) : null}
        </div>
      )}
      <div
        className={`rounded-2xl px-3.5 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-wrap break-words max-w-[85%] ${
          isUser
            ? "bg-amber-500/95 text-black rounded-br-sm"
            : "bg-zinc-900 text-white border border-zinc-800 rounded-bl-sm"
        }`}
      >
        {message.content}
      </div>
    </div>
  );
}

function LeadCaptureForm({ sessionId, onCaptured }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      const result = await jsonFetch(`/api/public/chat/capture-lead`, {
        method: "POST",
        body: JSON.stringify({
          session_id: sessionId,
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
        }),
      });
      toast.success("Thanks! A teammate will follow up shortly.");
      onCaptured(result);
    } catch (err) {
      toast.error(err.message || "Couldn't save your info — try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      data-testid="chat-lead-form"
      onSubmit={handleSubmit}
      className="space-y-2.5 p-3 bg-zinc-950 border-t border-zinc-800"
    >
      <div className="flex items-center gap-2 text-[11px] text-amber-300 uppercase tracking-[0.16em]">
        <Sparkles className="h-3 w-3" />
        Want a free estimate? Drop your contact
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="col-span-2">
          <Label htmlFor="lead-name" className="sr-only">Name</Label>
          <Input
            id="lead-name"
            data-testid="chat-lead-name"
            placeholder="Full name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={submitting}
            className="h-9 bg-black border-zinc-800 text-white placeholder:text-zinc-600"
            required
          />
        </div>
        <div className="col-span-2">
          <Label htmlFor="lead-email" className="sr-only">Email</Label>
          <Input
            id="lead-email"
            type="email"
            data-testid="chat-lead-email"
            placeholder="you@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            className="h-9 bg-black border-zinc-800 text-white placeholder:text-zinc-600"
            required
          />
        </div>
        <div className="col-span-2">
          <Label htmlFor="lead-phone" className="sr-only">Phone</Label>
          <Input
            id="lead-phone"
            data-testid="chat-lead-phone"
            placeholder="Phone (optional)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={submitting}
            className="h-9 bg-black border-zinc-800 text-white placeholder:text-zinc-600"
          />
        </div>
      </div>
      <Button
        type="submit"
        data-testid="chat-lead-submit"
        disabled={submitting}
        className="w-full h-9 bg-amber-500 hover:bg-amber-400 text-black font-semibold"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send to Black Ridge"}
      </Button>
      <div className="text-[10px] text-zinc-500 leading-relaxed">
        We only use your info to reply about your project. No spam.
      </div>
    </form>
  );
}

export default function PublicChatWidget() {
  const location = useLocation();
  const hidden = useMemo(() => classifyHidden(location.pathname), [location.pathname]);

  const [open, setOpen] = useState(false);
  const [bootError, setBootError] = useState("");
  const [session, setSession] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [shouldCaptureLead, setShouldCaptureLead] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [currentAgent, setCurrentAgent] = useState("pat-harper");
  const scrollRef = useRef(null);

  // Auto-scroll on new messages.
  useEffect(() => {
    if (!open) return;
    const node = scrollRef.current;
    if (node) {
      node.scrollTop = node.scrollHeight;
    }
  }, [session?.messages?.length, open, shouldCaptureLead, leadCaptured]);

  // Lazy-init session on first open.
  useEffect(() => {
    if (!open || session || bootError) return;
    let cancelled = false;
    (async () => {
      try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored) {
          try {
            const existing = await jsonFetch(`/api/public/chat/session/${encodeURIComponent(stored)}`);
            if (cancelled) return;
            setSession(existing);
            setCurrentAgent(existing.current_agent_slug || "pat-harper");
            setLeadCaptured(Boolean(existing.lead_captured));
            return;
          } catch (err) {
            if (err.status === 404) {
              window.localStorage.removeItem(STORAGE_KEY);
            }
          }
        }
        const fresh = await jsonFetch(`/api/public/chat/session`, {
          method: "POST",
          body: JSON.stringify({ source_page: window.location.pathname || "/" }),
        });
        if (cancelled) return;
        window.localStorage.setItem(STORAGE_KEY, fresh.id);
        setSession(fresh);
        setCurrentAgent(fresh.current_agent_slug || "pat-harper");
      } catch (err) {
        if (!cancelled) {
          setBootError(err.message || "Couldn't reach the chat right now.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, session, bootError]);

  async function handleSend(e) {
    e?.preventDefault();
    const text = draft.trim();
    if (!text || !session?.id || sending) return;
    setSending(true);

    // Optimistic user bubble
    const optimistic = {
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    setSession((prev) =>
      prev
        ? { ...prev, messages: [...(prev.messages || []), optimistic] }
        : prev,
    );
    setDraft("");

    try {
      const result = await jsonFetch(`/api/public/chat/message`, {
        method: "POST",
        body: JSON.stringify({ session_id: session.id, message: text }),
      });
      const reply = {
        role: "agent",
        agent_slug: result.agent_slug,
        content: result.reply,
        citations: result.citations || [],
        created_at: new Date().toISOString(),
      };
      setSession((prev) =>
        prev
          ? { ...prev, messages: [...(prev.messages || []), reply] }
          : prev,
      );
      setCurrentAgent(result.agent_slug || currentAgent);
      setShouldCaptureLead(Boolean(result.should_capture_lead));
      setLeadCaptured(Boolean(result.lead_captured));
    } catch (err) {
      toast.error(err.message || "Couldn't send — try again.");
    } finally {
      setSending(false);
    }
  }

  if (hidden) return null;

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          type="button"
          aria-label="Open chat with Black Ridge"
          data-testid="public-chat-bubble"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[60] grid h-14 w-14 place-items-center rounded-full
                     bg-gradient-to-br from-amber-400 to-amber-600 text-black
                     shadow-[0_10px_30px_rgba(0,0,0,0.35)]
                     transition-transform duration-150 hover:scale-105
                     focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
        >
          <MessageSquare className="h-6 w-6" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="public-chat-panel"
          className="dark fixed bottom-5 right-5 z-[60] flex w-[min(380px,calc(100vw-2rem))]
                     h-[min(560px,calc(100vh-2rem))] flex-col overflow-hidden rounded-2xl
                     border border-zinc-800 bg-black text-white shadow-[0_24px_60px_rgba(0,0,0,0.55)]"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-2 border-b border-zinc-800 bg-black px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="grid h-7 w-7 place-items-center rounded-md bg-gradient-to-br from-amber-400 to-amber-600 text-[10px] font-black text-black">
                BR
              </div>
              <div className="leading-tight">
                <div className="text-sm font-semibold text-white">Chat with Black Ridge</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 flex items-center gap-1">
                  <span>Live AI assistant</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Badge
                variant="outline"
                className="border-amber-500/40 text-amber-300 text-[10px] hidden sm:inline-flex"
                data-testid="public-chat-agent-badge"
              >
                {AGENT_LABELS[currentAgent] || "Pat"}
              </Badge>
              <Button
                size="sm"
                variant="ghost"
                aria-label="Close chat"
                data-testid="public-chat-close"
                onClick={() => setOpen(false)}
                className="h-7 w-7 p-0 text-zinc-400 hover:text-white"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Transcript */}
          <div
            ref={scrollRef}
            className="flex-1 space-y-3 overflow-y-auto bg-black p-3"
            data-testid="public-chat-transcript"
          >
            {bootError && (
              <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-[13px] text-red-200">
                {bootError}
              </div>
            )}
            {!bootError && !session && (
              <div className="flex items-center justify-center py-8 text-zinc-500">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Connecting…
              </div>
            )}
            {session?.messages?.map((m, idx) => (
              <MessageBubble key={`${idx}-${m.role}-${(m.content || "").slice(0, 12)}`} message={m} />
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-zinc-500 text-[12px] pl-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                {AGENT_LABELS[currentAgent] || "Pat"} is typing…
              </div>
            )}
            {leadCaptured && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-2.5 text-[12px] text-amber-200 flex items-center gap-2">
                <CheckCircle2 className="h-3.5 w-3.5 text-amber-300" />
                Got it — we&apos;ll be in touch shortly.
              </div>
            )}
          </div>

          {/* Lead form OR input */}
          {session && shouldCaptureLead && !leadCaptured ? (
            <LeadCaptureForm
              sessionId={session.id}
              onCaptured={() => {
                setLeadCaptured(true);
                setShouldCaptureLead(false);
              }}
            />
          ) : (
            <form
              onSubmit={handleSend}
              className="flex items-end gap-2 border-t border-zinc-800 bg-black p-3"
            >
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={session ? "Ask anything…" : "Connecting…"}
                disabled={!session || sending || Boolean(bootError)}
                data-testid="public-chat-input"
                className="h-10 flex-1 bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600"
              />
              <Button
                type="submit"
                disabled={!session || sending || !draft.trim()}
                data-testid="public-chat-send"
                className="h-10 bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                aria-label="Send message"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </form>
          )}

          {/* Footer disclaimer */}
          <div className="border-t border-zinc-900 bg-black px-3 py-1.5 text-[10px] text-zinc-600 flex items-center gap-1.5">
            <ShieldCheck className="h-3 w-3 text-zinc-500" />
            AI assistant. Real humans follow up. No specific pricing in chat.
          </div>
        </div>
      )}
    </>
  );
}
